import { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Button } from '@/components/ui/button'
import { Settings, Zap, Database, Key, ArrowRight } from 'lucide-react'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { useAuthStore } from '@/stores/authStore'
import { supabase } from '@/lib/supabase'
import { toast } from 'sonner'
import { Link } from 'react-router-dom'

interface ProjectSettings {
  id: string
  name: string
  enable_embeddings: boolean
}

export function SettingsPage() {
  const { user } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [project, setProject] = useState<ProjectSettings | null>(null)

  const loadProjectSettings = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('projects')
        .select('id, name, enable_embeddings')
        .eq('user_id', user?.id)
        .limit(1)

      if (error) {
        console.error('Error loading project settings:', error)
        toast.error('Failed to load settings', {
          description: error.message,
        })
        return
      }

      // Handle case where user has no projects yet
      if (!data || data.length === 0) {
        setProject(null)
        return
      }

      // TypeScript: Ensure first project exists before setting
      const firstProject = data[0]
      if (firstProject) {
        setProject(firstProject)
      } else {
        setProject(null)
      }
    } catch (error) {
      console.error('Error loading project settings:', error)
      toast.error('Failed to load settings', {
        description: 'An unexpected error occurred',
      })
    } finally {
      setLoading(false)
    }
  }

  const updateEmbeddingsSetting = async (enabled: boolean) => {
    if (!project) return

    setSaving(true)
    try {
      const { error } = await supabase
        .from('projects')
        .update({ enable_embeddings: enabled })
        .eq('id', project.id)

      if (error) {
        console.error('Error updating embeddings setting:', error)
        toast.error('Failed to update setting', {
          description: error.message,
        })
        return
      }

      setProject({ ...project, enable_embeddings: enabled })
      toast.success(
        enabled ? 'Automatic vector generation enabled' : 'Automatic vector generation disabled',
        {
          description: enabled
            ? 'New files will automatically generate vectors for semantic search'
            : 'New files will not generate vectors. Existing vectors remain unchanged.',
        }
      )
    } catch (error) {
      console.error('Error updating embeddings setting:', error)
      toast.error('Failed to update setting', {
        description: 'An unexpected error occurred',
      })
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    if (user?.id) {
      loadProjectSettings()
    }
  }, [user?.id])

  if (loading) {
    return (
      <div className='space-y-6'>
        <div>
          <h1 className='text-2xl font-semibold'>Settings</h1>
          <p className='text-sm text-muted-foreground mt-1'>
            Manage your project preferences and configurations
          </p>
        </div>
        <div className='flex items-center justify-center h-32'>
          <LoadingSpinner size='md' />
        </div>
      </div>
    )
  }

  // If no project exists, show setup message
  if (!project) {
    return (
      <div className='space-y-6'>
        {/* Header */}
        <div>
          <h1 className='text-2xl font-semibold'>Settings</h1>
          <p className='text-sm text-muted-foreground mt-1'>
            Manage your project preferences and configurations
          </p>
        </div>

        {/* Account Information */}
        <Card>
          <CardHeader>
            <div className='flex items-center space-x-2'>
              <Settings className='w-5 h-5 text-primary' />
              <CardTitle>Account Information</CardTitle>
            </div>
            <CardDescription>
              Your account details and project information for API integration
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <Label className='text-sm font-medium text-muted-foreground'>Username</Label>
                <p className='text-sm font-medium'>{user?.user_metadata?.user_name || 'Not set'}</p>
              </div>
              <div>
                <Label className='text-sm font-medium text-muted-foreground'>Email</Label>
                <p className='text-sm font-medium'>{user?.email || 'Not set'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Setup Required */}
        <Card>
          <CardHeader>
            <div className='flex items-center space-x-2'>
              <Key className='w-5 h-5 text-primary' />
              <CardTitle>Setup Required</CardTitle>
            </div>
            <CardDescription>
              Create your first API key to initialize your project and access settings
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-4'>
            <div className='text-center py-8'>
              <Key className='w-12 h-12 text-muted-foreground mx-auto mb-4' />
              <h3 className='text-lg font-medium mb-2'>No Project Found</h3>
              <p className='text-sm text-muted-foreground mb-6'>
                Your project will be automatically created when you generate your first API key.
                This unlocks all settings and features.
              </p>
              <Button asChild>
                <Link to='/api-keys'>
                  Create Your First API Key
                  <ArrowRight className='w-4 h-4 ml-2' />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div>
        <h1 className='text-2xl font-semibold'>Settings</h1>
        <p className='text-sm text-muted-foreground mt-1'>
          Manage your project preferences and configurations
        </p>
      </div>

      {/* Account Information */}
      <Card>
        <CardHeader>
          <div className='flex items-center space-x-2'>
            <Settings className='w-5 h-5 text-primary' />
            <CardTitle>Account Information</CardTitle>
          </div>
          <CardDescription>
            Your account details and project information for API integration
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-4'>
          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
            <div>
              <Label className='text-sm font-medium text-muted-foreground'>Username</Label>
              <p className='text-sm font-medium'>{user?.user_metadata?.user_name || 'Not set'}</p>
            </div>
            <div>
              <Label className='text-sm font-medium text-muted-foreground'>Email</Label>
              <p className='text-sm font-medium'>{user?.email || 'Not set'}</p>
            </div>
            <div>
              <Label className='text-sm font-medium text-muted-foreground'>Project Name</Label>
              <p className='text-sm font-medium'>{project?.name || 'Default Project'}</p>
            </div>
            <div>
              <Label className='text-sm font-medium text-muted-foreground'>Project ID</Label>
              <p className='text-sm font-mono text-muted-foreground'>
                {project?.id || 'Loading...'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* AI & Embeddings Settings */}
      <Card>
        <CardHeader>
          <div className='flex items-center space-x-2'>
            <Zap className='w-5 h-5 text-primary' />
            <CardTitle>AI & Embeddings</CardTitle>
          </div>
          <CardDescription>
            Configure automatic vector generation for semantic search capabilities
          </CardDescription>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='flex items-center justify-between'>
            <div className='space-y-1'>
              <Label htmlFor='enable-embeddings' className='text-sm font-medium'>
                Automatic Vector Generation
              </Label>
              <p className='text-sm text-muted-foreground'>
                Automatically generate vectors for new files to enable semantic search
              </p>
            </div>
            <Switch
              id='enable-embeddings'
              checked={project?.enable_embeddings ?? true}
              onCheckedChange={updateEmbeddingsSetting}
              disabled={saving}
            />
          </div>

          <Separator />

          <div className='space-y-2'>
            <h4 className='text-sm font-medium flex items-center space-x-2'>
              <Database className='w-4 h-4' />
              <span>How it works</span>
            </h4>
            <ul className='text-sm text-muted-foreground space-y-1 ml-6'>
              <li>• When enabled, new files automatically generate vectors for semantic search</li>
              <li>• Vectors are processed in the background using OpenAI embeddings</li>
              <li>• Disabling this setting won't delete existing vectors</li>
              <li>• You can always re-enable to start generating vectors for new files</li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
